/**
 * Forgot / reset password — the two public pages behind the emailed link.
 * Same shell and field styling as Login so the funnel looks like one flow.
 *
 * Network discipline: Forgot makes exactly one call per click (and the resend
 * button waits out the server's own 60s per-address gap); Reset makes NO call
 * until submit — there is no "check the link" request, a dead link is
 * reported by the submit itself (backend INVALID_TOKEN).
 *
 * Token hygiene (OWASP Forgot Password): the token is read once, then removed
 * from the address bar (history, screenshots, shoulder-surfing), and the page
 * runs with Referrer-Policy no-referrer so it can't leak to another origin.
 * It is parked in this history entry's state (never shown, never sent) so a
 * reload of the page still works instead of claiming the link expired.
 */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import MailOutline from '@mui/icons-material/MailOutline';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ThemeCtx,
  useTheme,
  useThemeState,
  THEMES,
  NavBar,
  Footer,
} from '@/pages/home/marketing-shell';
import { inputSx } from '@/pages/admin/Login';
import { requestPasswordReset, resetPassword } from '@/services/auth.api';
import { formatWait, retryAfterSeconds, useCooldown } from '@/hooks/useCooldown';
import { logout as logoutAction } from '@/store/slices/authSlice';
import { PASSWORD_MIN, passwordProblem } from '@/utils/passwordPolicy';

const RESEND_GAP_S = 60; // backend's per-address gap — resending sooner is silently dropped
const TOKEN_RE = /^[a-f0-9]{64}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TOKEN_STATE_KEY = 'resetToken';

/** A user-facing message for anything the reset endpoints can throw. */
function messageFor(error: any, fallback: string): string {
  if (!error?.response) return "Couldn't reach the server. Check your connection and try again.";
  return error.response.data?.error?.message || fallback;
}

function useRecoveryTheme() {
  const { theme } = useTheme();
  return useMemo(() => {
    const t = THEMES[theme];
    const isDark = theme === 'dark';
    const buttonSx = {
      py: 1.5,
      fontSize: '0.9375rem',
      fontWeight: 700,
      fontFamily: 'Manrope, sans-serif',
      borderRadius: '10px',
      background: t.green,
      color: isDark ? '#050C09' : '#fff',
      boxShadow: `0 6px 24px ${isDark ? 'rgba(61,220,132,0.35)' : 'rgba(37,211,102,0.4)'}`,
      '&:hover': { background: isDark ? '#34c476' : '#1fb955' },
      '&:disabled': { opacity: 0.5, color: isDark ? '#050C09' : '#fff' },
    };
    return { t, buttonSx, fieldSx: inputSx(t) };
  }, [theme]);
}

function Card({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
}) {
  const { t } = useRecoveryTheme();
  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 3, sm: 4 },
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        <Typography
          sx={{
            fontWeight: 800,
            mb: 1,
            letterSpacing: '-0.02em',
            fontFamily: 'Manrope, sans-serif',
            fontSize: '1.75rem',
            color: t.fg,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: t.mutedFg, mb: 3, lineHeight: 1.7 }}>
            {subtitle}
          </Typography>
        )}
        {children}
      </Box>
    </Box>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  const { t } = useRecoveryTheme();
  return (
    <Typography
      variant="caption"
      fontWeight={600}
      sx={{
        mb: 0.75,
        display: 'block',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: t.mutedFg,
      }}
    >
      {children}
    </Typography>
  );
}

function BackToSignIn() {
  const { t } = useRecoveryTheme();
  const navigate = useNavigate();
  return (
    <Typography sx={{ textAlign: 'center', mt: 3, fontSize: '0.875rem', color: t.mutedFg }}>
      <Box
        component="a"
        href="/login"
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          navigate('/login');
        }}
        sx={{ color: t.green, fontWeight: 700, textDecoration: 'none' }}
      >
        Back to sign in
      </Box>
    </Typography>
  );
}

// ── /forgot-password ─────────────────────────────────────────────────────────

function ForgotPasswordForm() {
  const { t, buttonSx, fieldSx } = useRecoveryTheme();
  const location = useLocation();
  // Prefilled from Login via router state (never a query param — no emails in URLs).
  const [email, setEmail] = useState(
    () => (location.state as { email?: string } | null)?.email ?? ''
  );
  const [sentTo, setSentTo] = useState<string | null>(null); // last address a link went to
  const [editing, setEditing] = useState(false); // "Use a different email" pressed
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);
  // Two different waits: `resend` is the server's per-ADDRESS gap (a different
  // email isn't held by it); `limited` is a 429 for this network (holds everything).
  const resend = useCooldown();
  const limited = useCooldown();

  const send = async () => {
    const value = email.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) {
      setError('Enter a valid email address.');
      return;
    }
    if (inFlight.current || limited.active) return;
    if (value === sentTo && resend.active) {
      setEditing(false); // that address just got a link — sending again now would be dropped
      return;
    }
    inFlight.current = true;
    setError('');
    setLoading(true);
    try {
      await requestPasswordReset(value);
      setSentTo(value);
      setEditing(false);
      resend.start(RESEND_GAP_S);
    } catch (err: any) {
      const wait = retryAfterSeconds(err);
      if (wait) {
        limited.start(wait);
        setError(`Too many reset requests from this network. Try again in ${formatWait(wait)}.`);
      } else {
        setError(messageFor(err, "We couldn't send the reset link. Please try again."));
      }
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };
  const resendWait = Math.max(resend.remaining, limited.remaining);

  if (sentTo && !editing) {
    return (
      <Card
        title="Check your email"
        subtitle={
          <>
            If an account exists for <strong>{sentTo}</strong>, we've sent a link to reset your
            password. The link works once and expires in 1 hour.
          </>
        }
      >
        <Typography variant="body2" sx={{ color: t.mutedFg, mb: 2 }}>
          Didn't get it? Check your spam folder, or send it again.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Button
          fullWidth
          variant="contained"
          disabled={loading || resendWait > 0}
          onClick={send}
          sx={buttonSx}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: 'inherit' }} />
          ) : resendWait > 0 ? (
            `Resend in ${formatWait(resendWait)}`
          ) : (
            'Resend link'
          )}
        </Button>
        <Button
          fullWidth
          variant="text"
          onClick={() => {
            setEditing(true);
            setError('');
          }}
          sx={{ mt: 1.5, color: t.green, fontWeight: 600, textTransform: 'none' }}
        >
          Use a different email
        </Button>
        <BackToSignIn />
      </Card>
    );
  }

  return (
    <Card
      title="Forgot your password?"
      subtitle="Enter your account's email and we'll send you a link to set a new one."
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      <Box
        component="form"
        noValidate
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          void send();
        }}
      >
        <Label>Email</Label>
        <TextField
          fullWidth
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          autoFocus
          placeholder="you@company.com"
          sx={{ mb: 3, ...fieldSx }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MailOutline sx={{ fontSize: 18, color: t.mutedFg }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading || limited.active || !email.trim()}
          sx={buttonSx}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: 'inherit' }} />
          ) : limited.active ? (
            `Try again in ${formatWait(limited.remaining)}`
          ) : (
            'Send reset link'
          )}
        </Button>
      </Box>
      <BackToSignIn />
    </Card>
  );
}

// ── /reset-password?token=… ──────────────────────────────────────────────────

function InvalidLink() {
  const { buttonSx } = useRecoveryTheme();
  const navigate = useNavigate();
  return (
    <Card
      title="This link has expired"
      subtitle="Reset links work once and last 1 hour, and requesting a new one cancels older links. Request a fresh link to continue."
    >
      <Button
        fullWidth
        variant="contained"
        onClick={() => navigate('/forgot-password')}
        sx={buttonSx}
      >
        Request a new link
      </Button>
      <BackToSignIn />
    </Card>
  );
}

function ResetPasswordForm() {
  const { t, buttonSx, fieldSx } = useRecoveryTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  // Read ONCE: from the URL on arrival, or from this history entry's state on a
  // reload (the effect below moves it there and strips it from the address bar).
  const [token] = useState(
    () =>
      new URLSearchParams(window.location.search).get('token') ??
      (location.state as Record<string, string> | null)?.[TOKEN_STATE_KEY] ??
      ''
  );
  const [invalid, setInvalid] = useState(() => !TOKEN_RE.test(token));
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);
  const cooldown = useCooldown();

  useEffect(() => {
    if (location.search)
      navigate(location.pathname, {
        replace: true,
        state: token ? { [TOKEN_STATE_KEY]: token } : null,
      });
    // Referrer-Policy: no-referrer for as long as this page is open.
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => meta.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (invalid) return <InvalidLink />;

  const problem = passwordProblem(password);
  const mismatch = confirm.length > 0 && confirm !== password;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (problem || confirm !== password || inFlight.current || cooldown.active) return;
    inFlight.current = true;
    setError('');
    setLoading(true);
    try {
      await resetPassword(token, password);
      // Every session was just revoked server-side — drop this browser's copy
      // too, locally (no /logout call: the session it would end is already gone).
      dispatch(logoutAction());
      navigate('/login', {
        replace: true,
        state: {
          notice:
            'Password updated. Sign in with your new password — every device that was signed in has been signed out.',
        },
      });
    } catch (err: any) {
      const wait = retryAfterSeconds(err);
      if (err?.response?.data?.error?.code === 'INVALID_TOKEN') {
        navigate(location.pathname, { replace: true }); // dead token — don't keep it for reloads
        setInvalid(true);
      } else if (wait) {
        cooldown.start(wait);
        setError(`Too many attempts. Try again in ${formatWait(wait)}.`);
      } else {
        setError(messageFor(err, "We couldn't update your password. Please try again."));
      }
      inFlight.current = false;
      setLoading(false);
    }
  };

  const eye = (
    <InputAdornment position="end">
      <IconButton
        size="small"
        onClick={() => setShow((s) => !s)}
        edge="end"
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? (
          <VisibilityOff sx={{ fontSize: 18, color: t.mutedFg }} />
        ) : (
          <Visibility sx={{ fontSize: 18, color: t.mutedFg }} />
        )}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Card
      title="Set a new password"
      subtitle="Choose a password you don't use anywhere else. You'll be signed out of every device."
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      <Box component="form" noValidate onSubmit={submit}>
        <Label>New password</Label>
        <TextField
          fullWidth
          type={show ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onBlur={() => password && setTouched(true)}
          autoComplete="new-password"
          autoFocus
          error={touched && !!problem}
          helperText={touched && problem ? problem : `At least ${PASSWORD_MIN} characters`}
          FormHelperTextProps={{ sx: { color: touched && problem ? undefined : t.mutedFg, mx: 0 } }}
          sx={{ mb: 2, ...fieldSx }}
          InputProps={{ endAdornment: eye }}
        />
        <Label>Confirm new password</Label>
        <TextField
          fullWidth
          type={show ? 'text' : 'password'}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          error={mismatch || (touched && confirm !== password)}
          helperText={mismatch || (touched && confirm !== password) ? "Passwords don't match" : ' '}
          FormHelperTextProps={{ sx: { mx: 0 } }}
          sx={{ mb: 2, ...fieldSx }}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading || cooldown.active || !password || !confirm}
          sx={buttonSx}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: 'inherit' }} />
          ) : cooldown.active ? (
            `Try again in ${formatWait(cooldown.remaining)}`
          ) : (
            'Update password'
          )}
        </Button>
      </Box>
      <BackToSignIn />
    </Card>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const themeCtxValue = useThemeState('light');
  const t = THEMES[themeCtxValue.theme];
  return (
    <ThemeCtx.Provider value={themeCtxValue}>
      <div
        style={{
          minHeight: '100vh',
          background: t.bg,
          color: t.fg,
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <NavBar />
        <main style={{ paddingTop: 64 }}>{children}</main>
        <Footer />
      </div>
    </ThemeCtx.Provider>
  );
}

export function ForgotPassword() {
  return (
    <Shell>
      <ForgotPasswordForm />
    </Shell>
  );
}

export function ResetPassword() {
  return (
    <Shell>
      <ResetPasswordForm />
    </Shell>
  );
}
